import React, { useState } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  SportsSoccer as SportsSoccerIcon,
} from "@mui/icons-material";

import TableActionsMenu from "../../../../Components/TableActionsMenu/TableActionsMenu";
import useStyles from "../../../../Components/Form/formStyles";
import styles from "./ClubsTable.styles";
import { useTranslation } from "../../../../context/Internationalization";
import { toTitleCase } from "../../../../utility/Utils";

const MAX_NAME_LENGTH = 25;
const MAX_LINKNAME_LENGTH = 32;

const ClubsTableRow = ({ club, menuActions, lastElementRef }) => {
  const { t } = useTranslation();
  const _styles = useStyles();
  const [openPhotoModal, setOpenPhotoModal] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuActionsOpen = Boolean(menuAnchorEl);

  const handleClickActionMenu = (event) => setMenuAnchorEl(event.currentTarget);
  const handleCloseActionMenu = () => setMenuAnchorEl(null);
  const handleFieldPhotoClick = () => setOpenPhotoModal(true);
  const handleClosePhotoModal = () => setOpenPhotoModal(false);

  const formatUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http://") || url.startsWith("https://")
      ? url
      : `https://${url}`;
  };

  const getHostname = (url) => {
    try {
      return new URL(formatUrl(url)).hostname;
    } catch {
      return url;
    }
  };

  const fullName = `${club?.name || ""}${
    club?.abbreviation ? ` (${club.abbreviation})` : ""
  }`;
  const isLong = fullName.length > MAX_NAME_LENGTH;
  const truncated =
    fullName.length > MAX_NAME_LENGTH
      ? `${fullName.substring(0, MAX_NAME_LENGTH)}…`
      : fullName;

  const linkName = club?.linkName || "";
  const hasLinkName = Boolean(linkName);
  const linkNameText = linkName ? `${linkName}` : "";
  const linkNameIsLong = linkNameText.length > MAX_LINKNAME_LENGTH;
  const linkNameTruncated =
    linkNameText.length > MAX_LINKNAME_LENGTH
      ? `${linkNameText.substring(0, MAX_LINKNAME_LENGTH)}…`
      : linkNameText;

  return (
    <>
      <TableRow
        ref={lastElementRef}
        key={club._id}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        {/* Club Logo & Name (Abbrv) + linkName */}
        <TableCell component="th" scope="row">
          <Stack alignItems="center" direction="row" spacing={1}>
            <Avatar
              style={styles.clubLogo}
              src={club?.logo || undefined}
              alt={club?.name}
            >
              {!club?.logo && (club?.name?.[0] || "?").toUpperCase()}
            </Avatar>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" component="h6">
                {isLong ? (
                  <Tooltip title={fullName} arrow>
                    <Typography
                      variant="inherit"
                      component="span"
                      noWrap
                      sx={styles.wrappedClubName}
                    >
                      {truncated}
                    </Typography>
                  </Tooltip>
                ) : (
                  fullName
                )}
              </Typography>

              {hasLinkName && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2, mt: 0.25, maxWidth: 280 }}
                  noWrap
                  component="div"
                >
                  {linkNameIsLong ? (
                    <Tooltip title={linkNameText} arrow>
                      <span>{linkNameTruncated}</span>
                    </Tooltip>
                  ) : (
                    linkNameText
                  )}
                </Typography>
              )}
            </Box>
          </Stack>
        </TableCell>

        {/* Club Colors */}
        <TableCell>
          <Box display="flex" flexDirection="row" alignItems="center">
            {club?.color?.primary && (
              <div
                className={_styles.colorDiv}
                style={{ backgroundColor: club.color.primary }}
              />
            )}
            {club?.color?.secondary && (
              <div
                className={_styles.colorDiv}
                style={{ backgroundColor: club.color.secondary }}
              />
            )}
            {club?.color?.tertiary && (
              <div
                className={_styles.colorDiv}
                style={{ backgroundColor: club.color.tertiary }}
              />
            )}
          </Box>
        </TableCell>

        {/* Country */}
        <TableCell>
          <Typography variant="subtitle1" component="span">
            {club.country}
          </Typography>
        </TableCell>

        {/* Founded Year */}
        <TableCell>
          <Typography variant="subtitle1" component="span">
            {club.founded || ""}
          </Typography>
        </TableCell>

        {/* Contact Info */}
        <TableCell>
          <Box display="flex" flexDirection="column">
            {club?.contactInfo?.website && (
              <Link
                href={formatUrl(club.contactInfo.website)}
                target="_blank"
                rel="noopener noreferrer"
                variant="subtitle2"
                color="primary"
                sx={{ textDecoration: "none" }}
              >
                <Stack direction="row" alignItems="center">
                  <LanguageIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {getHostname(club.contactInfo.website)}
                </Stack>
              </Link>
            )}
            {club?.contactInfo?.email && (
              <Link
                href={`mailto:${club.contactInfo.email}`}
                variant="body2"
                color="text.secondary"
                sx={{ textDecoration: "none" }}
              >
                <Stack direction="row" alignItems="center">
                  <EmailIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {club.contactInfo.email}
                </Stack>
              </Link>
            )}
          </Box>
        </TableCell>

        {/* Field Info */}
        <TableCell>
          {club?.fieldInfo && (
            <Box display="flex" alignItems="center">
              {club?.fieldInfo?.photo && (
                <Box mr={1}>
                  <img
                    src={club.fieldInfo.photo}
                    alt={club.fieldInfo.name}
                    loading="lazy"
                    onClick={handleFieldPhotoClick}
                    style={styles.fieldPhoto}
                  />
                </Box>
              )}
              <Box display="flex" flexDirection="column">
                {club?.fieldInfo?.name && (
                  <Box display="flex" alignItems="center">
                    <SportsSoccerIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography variant="subtitle1" component="span">
                      {toTitleCase(club.fieldInfo.name) || ""}
                    </Typography>
                  </Box>
                )}
                {club?.fieldInfo?.location?.address && (
                  <Box display="flex" alignItems="center">
                    <LocationOnIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography variant="subtitle1" component="span">
                      {club.fieldInfo.location.address}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </TableCell>

        {/* Location */}
        <TableCell>
          <Typography variant="subtitle1" component="span">
            {club?.location?.address}
          </Typography>
        </TableCell>

        {/* Participates to PlayerDex FC? */}
        <TableCell>
          <Typography variant="subtitle1" component="span">
            {club?.isClaimed ? t("yes") : t("no")}
          </Typography>
        </TableCell>

        {/* Actions */}
        <TableCell>
          <TableActionsMenu
            handleClickAction={handleClickActionMenu}
            handleCloseActionMenu={handleCloseActionMenu}
            rowData={club}
            actions={menuActions}
            anchorEl={menuAnchorEl}
            open={menuActionsOpen}
          />
        </TableCell>
      </TableRow>

      {/* Field Photo Modal */}
      <Dialog
        open={openPhotoModal}
        onClose={handleClosePhotoModal}
        maxWidth="md"
      >
        <DialogContent sx={styles.dialogContent}>
          <IconButton onClick={handleClosePhotoModal} sx={styles.closeIcon}>
            <CloseIcon />
          </IconButton>
          <img
            src={club?.fieldInfo?.photo}
            alt={club?.fieldInfo?.name}
            style={styles.imgDialog}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

ClubsTableRow.propTypes = {};

export default ClubsTableRow;
